import { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
} from '@mui/material';
import type { ZoomData } from '@mui/x-charts-pro/models';
import { skipToken } from '@reduxjs/toolkit/query';
import DateRange from '@/components/date/DateRange';
import { dashboardMobileWidthValue, mobileWidth } from '@/constants';
import { DASHBOARD_WIDGET_DATE_PICKER_SX } from '@/constants/dashboardWidgetDatePickerSx';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useLocalization } from '@/hooks/useLocalization';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import type { NetWorthChangePeriod } from '@/interfaces/netWorthChanges';
import type { StandardWidgetProps } from '@/models/WidgetModels';
import { defaultNetWorthChangeRange } from '@/modules/advisor/netWorthChanges/netWorthChangeRanges';
import {
  type NetWorthChangeSelection,
  periodForSelection,
} from '@/modules/advisor/netWorthChanges/netWorthChangeSelection';
import { useNetWorthChangeRangePicker } from '@/modules/advisor/netWorthChanges/useNetWorthChangeRangePicker';
import { useGetWealthReportedTrendQuery } from '@/store/advisorWealthTrendApi';
import { changeTone } from '@/utils/changeTone';
import { formatNumberToCurrency } from '@/utils/currency';
import { APP_DATE_FORMAT, formatAppDate } from '@/utils/date';
import { ContentContainer } from '@/widgets/contentContainer';
import WealthTrendLineChart, {
  WEALTH_TREND_X_AXIS_ID,
} from './WealthTrendLineChart';
import WealthTrendPopover, {
  type WealthTrendPopoverPoint,
} from './WealthTrendPopover';
import {
  MANAGED_SERIES_ID,
  type WealthTrendPointRef,
  buildWealthTrendChart,
  hasTrendPoints,
  snapZoomToDays,
  valueAt,
  zoomWindow,
} from './wealthTrendChartData';
import { trendNetChange, type WealthTrendWindow } from './wealthTrendMath';

/**
 * The chart's unzoomed state. Controlled zoom needs an entry for the axis to
 * change: started from an empty list, a drag was ignored and nothing zoomed.
 */
const FULL_RANGE: readonly ZoomData[] = [
  { axisId: WEALTH_TREND_X_AXIS_ID, start: 0, end: 100 },
];

/**
 * The range the date box shows: the zoomed days when zoomed, as legacy did,
 * otherwise the chosen range.
 */
function displayedPeriod(
  period: NetWorthChangePeriod | null,
  window: WealthTrendWindow | null,
): NetWorthChangePeriod | null {
  if (!window) return period;
  return {
    startDate: formatAppDate(window.start, { format: APP_DATE_FORMAT }),
    endDate: formatAppDate(window.end, { format: APP_DATE_FORMAT }),
  };
}

/** A touch screen: phones and tablets, as legacy told apart. */
const TOUCH_POINTER = '(pointer: coarse)';

/**
 * How to zoom, as legacy's chart said: drag with a mouse, pinch on a touch
 * screen. Shown at every width, since a narrow window still has a mouse.
 */
function zoomHint(
  t: ReturnType<typeof useLocalization>['t'],
  isTouch: boolean,
): string {
  if (isTouch)
    return t('wealthTrend.zoomHintTouch', 'Pinch the chart to zoom in');
  return t('wealthTrend.zoomHint', 'Click and drag in the chart to zoom in');
}

/**
 * Advisor dashboard widget charting the managed and held-away totals across
 * the advisor's book over a period: the Beta build of legacy's Wealth Reported
 * Trend (`advisor-historical-aum`, IU-324). The totals are scoped server-side
 * to the clients the advisor can see.
 *
 * Net Change is the combined move across both series over the range, or over
 * the zoomed days when zoomed; hiding a line in the legend leaves it alone.
 * Zooming shows the zoomed days in the date box as a custom range, as legacy
 * did; choosing a range resets the zoom, which is the only way to reset it.
 *
 * The range defaults to This Quarter and offers the same presets as the other
 * advisor widgets. Points open a detail popover on desktop; on mobile, as in
 * legacy, a plain tooltip shows instead.
 */
export default function WealthReportedTrendWidget({
  title,
  isWidget = false,
  isHalfWidth = false,
  container = 'widget',
}: StandardWidgetProps = {}) {
  const { t } = useLocalization();
  const isMobile = useMediaQuery(mobileWidth);
  const isTouch = useMediaQuery(TOUCH_POINTER);
  const authLoaded = useAuthIsLoaded();
  const { ref: widgetWidthRef, isBelow: isBelowMobileWidth } =
    useWidgetWidthIsBelow(isWidget, dashboardMobileWidthValue);
  const isCompact =
    isMobile || (isWidget && isHalfWidth) || (isWidget && isBelowMobileWidth);

  const [selection, setSelectionState] = useState<NetWorthChangeSelection>(
    () => ({ kind: 'preset', range: defaultNetWorthChangeRange() }),
  );
  const [zoomData, setZoomData] = useState<readonly ZoomData[]>(FULL_RANGE);
  const [openPoint, setOpenPoint] = useState<WealthTrendPopoverPoint | null>(
    null,
  );

  const setSelection = useCallback((next: NetWorthChangeSelection) => {
    setSelectionState(next);
    setZoomData(FULL_RANGE);
    setOpenPoint(null);
  }, []);
  const picker = useNetWorthChangeRangePicker(selection, setSelection);

  const period: NetWorthChangePeriod | null = useMemo(
    () => periodForSelection(selection, picker.today),
    [selection, picker.today],
  );

  const { currentData, isFetching, isError, refetch } =
    useGetWealthReportedTrendQuery(authLoaded && period ? period : skipToken);

  const chart = useMemo(
    () => buildWealthTrendChart(currentData?.Records ?? []),
    [currentData],
  );

  const handleZoomChange = useCallback(
    (next: ZoomData[]) => {
      setZoomData(
        next.map((entry) => {
          if (entry.axisId !== WEALTH_TREND_X_AXIS_ID) return entry;
          return { ...entry, ...snapZoomToDays(chart.days, entry) };
        }),
      );
    },
    [chart.days],
  );

  const zoom = zoomData.find(
    (entry) => entry.axisId === WEALTH_TREND_X_AXIS_ID,
  );
  const zoomedDays = zoomWindow(chart.days, zoom ?? null);
  const netChange = trendNetChange(currentData?.Records ?? [], zoomedDays);
  const hasNoTrend = isError && !currentData;
  const isLoading = !authLoaded || (isFetching && !currentData);
  const netChangeTone = changeTone(hasNoTrend ? 0 : netChange);

  const netChangeText = () => {
    if (isLoading) {
      return (
        <Skeleton
          width={160}
          data-testid='wealth-trend-net-change-loading'
        />
      );
    }
    if (hasNoTrend) return t('common.emdash', '—');
    return formatNumberToCurrency(netChange);
  };
  const shownPeriod = displayedPeriod(period, zoomedDays);

  const closePopover = useCallback(() => {
    setOpenPoint(null);
  }, []);

  const handleOpenPoint = useCallback(
    (ref: WealthTrendPointRef, anchor: { top: number; left: number }) => {
      const value = valueAt(chart, ref);
      if (value === null) return;
      const day = chart.days[ref.dayIndex];
      setOpenPoint({
        isHome: ref.seriesId === MANAGED_SERIES_ID,
        day,
        value,
        anchor,
      });
    },
    [chart],
  );

  const widgetTitle =
    title ?? t('widgetWealthReportedTrend', 'Wealth Reported Trend');

  const renderBody = () => {
    if (isError) {
      return (
        <Stack
          alignItems='flex-start'
          gap={1.5}
        >
          <Typography color='error'>
            {t('errorLoadingData', 'Error loading data.')}
          </Typography>
          <Button
            variant='outlined'
            disabled={isFetching}
            onClick={() => {
              void refetch();
            }}
          >
            {t('retry', 'Retry')}
          </Button>
        </Stack>
      );
    }
    if (isLoading) {
      return (
        <Skeleton
          variant='rounded'
          height={320}
          data-testid='wealth-trend-loading'
        />
      );
    }
    if (!period || !hasTrendPoints(chart)) {
      return (
        <Typography color='text.secondary'>
          {t(
            'wealthTrend.empty',
            'No reported wealth for your clients over this range.',
          )}
        </Typography>
      );
    }
    return (
      <WealthTrendLineChart
        chart={chart}
        zoomData={zoomData}
        onZoomChange={handleZoomChange}
        showPopover={!isMobile}
        onOpenPoint={handleOpenPoint}
        isCompact={isCompact}
        legendHint={t('wealthTrend.legendHint', 'Click item to hide')}
        isTouch={isTouch}
        onDragStart={closePopover}
      />
    );
  };

  return (
    <ContentContainer
      ref={widgetWidthRef}
      container={container}
      sx={{ width: '100%' }}
    >
      <Typography
        variant='h5'
        sx={{ mb: 1.5 }}
      >
        {widgetTitle}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Box sx={DASHBOARD_WIDGET_DATE_PICKER_SX}>
          <DateRange
            startDate={picker.draft?.startDate ?? shownPeriod?.startDate}
            endDate={picker.draft?.endDate ?? shownPeriod?.endDate}
            onChange={picker.onChange}
            onClose={picker.onClose}
            shortcutItems={picker.shortcutItems}
            selectedShortcutLabel={
              zoomedDays ? '' : picker.selectedShortcutLabel
            }
            maxDate={picker.maxDate}
            useWidgetNarrowIconField={isWidget && isBelowMobileWidth}
          />
        </Box>
      </Box>

      <Stack
        direction={isCompact ? 'column' : 'row'}
        justifyContent='space-between'
        alignItems={isCompact ? 'flex-start' : 'flex-end'}
        gap={1}
        sx={{ mb: 1 }}
      >
        <Box>
          <Typography sx={{ fontWeight: 'bold' }}>
            {t('wealthTrend.netChange', 'Net Change')}
          </Typography>
          <Typography
            variant='h4'
            sx={{ color: netChangeTone, fontWeight: 'bold' }}
            data-testid='wealth-trend-net-change'
          >
            {netChangeText()}
          </Typography>
        </Box>
        <Typography
          variant='caption'
          color='text.secondary'
        >
          {zoomHint(t, isTouch)}
        </Typography>
      </Stack>

      {renderBody()}

      <WealthTrendPopover
        point={openPoint}
        onClose={closePopover}
      />
    </ContentContainer>
  );
}
